import { t } from 'i18next';
import { Layers } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatUtils } from '@/lib/utils';
import { AdminHealthOverview } from '@activepieces/shared';

import { FAIR_QUEUE_ROUTE, SECONDS_TO_MS } from './constants';
import { formatBytes } from './format-bytes';
import { PanelTitle } from './panel-title';
import { queueLabel } from './queue-labels';

const EMPTY_VALUE = '—';

const isQueueBusy = (queue: AdminHealthOverview['queues'][number]): boolean =>
  queue.jobsWaiting > 0 || queue.jobsActive > 0;

// Redis memory belongs next to the queues: BullMQ jobs and the fair-queue
// accounting are what fill it, and an abnormal growth here is a key leak.
const RedisMemoryLine = ({
  redisMemory,
}: {
  redisMemory: AdminHealthOverview['redisMemory'];
}) => {
  if (!redisMemory) {
    return null;
  }
  return (
    <div className="flex items-baseline justify-between gap-4 px-4 py-3 border-t text-sm">
      <span className="text-muted-foreground">{t('Redis memory')}</span>
      <span className="font-medium tabular-nums">
        {formatBytes(redisMemory.usedBytes)}
        {redisMemory.maxBytes !== null &&
          t(' / {max}', { max: formatBytes(redisMemory.maxBytes) })}
        {redisMemory.peakBytes !== null &&
          t(' · peak {peak}', { peak: formatBytes(redisMemory.peakBytes) })}
      </span>
    </div>
  );
};

export const RuntimePanel = ({ health }: { health: AdminHealthOverview }) => {
  const hasBacklog = health.queues.some(isQueueBusy);

  return (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b">
        <PanelTitle icon={Layers} title={t('Queues')} />
        <Link
          to={FAIR_QUEUE_ROUTE}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {t('Fair queue details')}
        </Link>
      </div>

      {hasBacklog ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('Queue')}</TableHead>
                <TableHead className="text-right">{t('Waiting')}</TableHead>
                <TableHead className="text-right">{t('Running')}</TableHead>
                <TableHead className="text-right">{t('Oldest job')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {health.queues.map((queue) => (
                <TableRow key={queue.queueName}>
                  <TableCell className="font-medium">
                    {queueLabel(queue.queueName)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUtils.formatNumber(queue.jobsWaiting)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUtils.formatNumber(queue.jobsActive)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {queue.jobsWaiting > 0
                      ? formatUtils.formatDuration(
                          queue.oldestJobAgeSeconds * SECONDS_TO_MS,
                          true,
                        )
                      : EMPTY_VALUE}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="px-4 py-6 text-sm text-muted-foreground">
          {t('All queues are empty.')}
        </p>
      )}
      <RedisMemoryLine redisMemory={health.redisMemory} />
    </Card>
  );
};
